// src/components/layout/Header.tsx

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { MagnifyingGlassIcon, Bars3Icon, MicrophoneIcon, StopIcon } from '@heroicons/react/24/outline';
import UserNav from './UserNav';
import { useSearch } from '../../hooks/useSearch';
import SearchSuggestions from '../SearchSuggestions';
import { sendLocalDebugIngest } from '../../lib/debugIngest';
import { apiClient } from '../../lib/apiClient';

interface HeaderProps {
  onMobileMenuToggle: () => void;
}

const Header: React.FC<HeaderProps> = ({ onMobileMenuToggle }) => {
  const [query, setQuery] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(false);
  const { handleSearch, isLoading, error } = useSearch();
  const [voiceState, setVoiceState] = useState<'idle' | 'recording' | 'transcribing'>('idle');
  const [voiceError, setVoiceError] = useState<string | null>(null);

  const inputRef = useRef<HTMLInputElement | null>(null);
  const mediaRecorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<BlobPart[]>([]);
  const streamRef = useRef<MediaStream | null>(null);

  const canUseMediaRecorder = useMemo(() => {
    return typeof window !== 'undefined' && typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia && typeof MediaRecorder !== 'undefined';
  }, []);

  const cleanupStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
  };

  useEffect(() => {
    return () => {
      try {
        if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
          mediaRecorderRef.current.stop();
        }
      } catch {
        // ignore
      }
      cleanupStream();
    };
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      sendLocalDebugIngest({
        hypothesisId: 'H1',
        location: 'Header.tsx:22',
        message: 'Header submit query',
        data: {
          submittedQuery: query.trim(),
          localQueryValue: query,
          isLoading,
        },
      });
      // Use MCP service to intelligently route the query
      handleSearch(query.trim());
      setQuery(''); // Clear the input after submission
      setShowSuggestions(false); // Hide suggestions after submit
    }
  };

  const startRecording = async () => {
    if (!canUseMediaRecorder) {
      setVoiceError('Voice input is not supported in this browser.');
      return;
    }
    if (voiceState !== 'idle') {
      return;
    }

    setVoiceError(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      chunksRef.current = [];

      const preferredMimeTypes = ['audio/webm;codecs=opus', 'audio/webm'];
      const mimeType = preferredMimeTypes.find((t) => MediaRecorder.isTypeSupported(t));

      const recorder = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      mediaRecorderRef.current = recorder;

      recorder.ondataavailable = (evt: BlobEvent) => {
        if (evt.data && evt.data.size > 0) {
          chunksRef.current.push(evt.data);
        }
      };

      recorder.onstop = async () => {
        const blob = new Blob(chunksRef.current, { type: recorder.mimeType || 'audio/webm' });
        cleanupStream();

        if (!blob.size) {
          setVoiceState('idle');
          setVoiceError('No audio captured.');
          return;
        }

        setVoiceState('transcribing');
        try {
          const result = await (apiClient as any).transcribeVoice(blob);
          const transcript = String(result?.transcript || '').trim();
          if (!transcript) {
            setVoiceError('No speech detected.');
            setVoiceState('idle');
            return;
          }

          setQuery(transcript);
          setShowSuggestions(false);
          inputRef.current?.focus();
          setVoiceState('idle');
        } catch (err: any) {
          const message =
            err?.response?.data?.detail ||
            err?.message ||
            'Failed to transcribe audio.';
          setVoiceError(String(message));
          setVoiceState('idle');
        }
      };

      recorder.start();
      setVoiceState('recording');
    } catch (err: any) {
      const message = err?.name === 'NotAllowedError'
        ? 'Microphone permission was denied.'
        : (err?.message || 'Failed to access microphone.');
      setVoiceError(String(message));
      cleanupStream();
      setVoiceState('idle');
    }
  };

  const stopRecording = () => {
    const recorder = mediaRecorderRef.current;
    if (!recorder) {
      setVoiceState('idle');
      cleanupStream();
      return;
    }
    if (recorder.state === 'inactive') {
      setVoiceState('idle');
      cleanupStream();
      return;
    }
    try {
      recorder.stop();
    } catch {
      setVoiceState('idle');
      cleanupStream();
    }
  };

  const toggleRecording = async () => {
    if (voiceState === 'recording') {
      stopRecording();
      return;
    }
    if (voiceState === 'idle') {
      await startRecording();
    }
  };

  const handleFocus = () => {
    if (!isLoading) {
      setShowSuggestions(true);
    }
  };

  const handleSelectExample = (example: string) => {
    setQuery(example);
    // Optionally auto-submit after selecting an example
    // handleSearch(example);
  };

  return (
    <header className="sticky top-0 z-10 flex h-16 flex-shrink-0 items-center justify-between border-b border-border bg-background px-4 sm:px-6 lg:px-8">
      {/* Mobile menu button */}
      <button
        type="button"
        className="lg:hidden -m-2.5 p-2.5 text-muted-foreground hover:text-foreground"
        onClick={onMobileMenuToggle}
      >
        <span className="sr-only">Open sidebar</span>
        <Bars3Icon className="h-6 w-6" aria-hidden="true" />
      </button>

      {/* AI Command Bar */}
      <form onSubmit={handleSubmit} className="relative w-full max-w-2xl ml-4 lg:ml-0">
        <label htmlFor="search" className="sr-only">Ask Edyt anything</label>
        <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
          <MagnifyingGlassIcon className={`h-5 w-5 ${isLoading ? 'text-primary animate-pulse' : 'text-muted-foreground'}`} />
        </div>
        <input
          id="search"
          name="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={handleFocus}
          disabled={isLoading || voiceState === 'transcribing'}
          autoComplete="off"
          ref={inputRef}
          className={`block w-full rounded-md border border-input bg-background py-2 pl-10 pr-12 text-sm placeholder-muted-foreground focus:border-primary focus:text-foreground focus:placeholder-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary sm:text-sm ${(isLoading || voiceState === 'transcribing') ? 'opacity-50 cursor-not-allowed' : ''}`}
          placeholder={isLoading ? "Processing..." : "Ask Edyt anything... (e.g., 'Generate QBR for Apple', 'What did HubSpot say about pricing?')"}
          type="text"
        />

        <div className="absolute inset-y-0 right-0 flex items-center pr-2">
          <button
            type="button"
            onClick={toggleRecording}
            disabled={!canUseMediaRecorder || isLoading || voiceState === 'transcribing'}
            className={`inline-flex items-center justify-center rounded-md p-2 transition-colors ${
              voiceState === 'recording'
                ? 'text-destructive hover:text-destructive/80'
                : 'text-muted-foreground hover:text-foreground'
            } ${(!canUseMediaRecorder || isLoading || voiceState === 'transcribing') ? 'opacity-50 cursor-not-allowed' : ''}`}
            aria-label={voiceState === 'recording' ? 'Stop voice recording' : 'Start voice recording'}
            title={voiceState === 'recording' ? 'Stop recording' : 'Voice query'}
          >
            {voiceState === 'recording' ? (
              <StopIcon className="h-5 w-5" aria-hidden="true" />
            ) : (
              <MicrophoneIcon className="h-5 w-5" aria-hidden="true" />
            )}
          </button>
        </div>

        <SearchSuggestions
          isVisible={showSuggestions}
          onClose={() => setShowSuggestions(false)}
          onSelectExample={handleSelectExample}
        />
        {voiceError && (
          <div className="absolute top-full left-0 right-0 mt-1 p-2 bg-destructive/10 border border-destructive/20 rounded-md text-sm text-destructive">
            {voiceError}
          </div>
        )}
        {error && (
          <div className="absolute top-full left-0 right-0 mt-1 p-2 bg-destructive/10 border border-destructive/20 rounded-md text-sm text-destructive">
            {error}
          </div>
        )}
      </form>

      {/* User navigation */}
      <UserNav />
    </header>
  );
};

export default Header;






